import {
  AdminTodoResponse,
  BackfillQueueResponse,
  QueueHealthStatus,
  SchedulerHealthStatus,
  SnapshotQueueResponse,
} from "../../../../services";
import {
  BLOCKED_JOBS_LINK,
  CORPORATE_ACTIONS_QUEUE_LINK,
  DEFERRED_CORPORATE_ACTIONS_QUEUE_LINK,
  INSTRUMENT_FIXES_QUEUE_LINK,
  JOBS_PATH,
  SCHEDULERS_PATH,
  TICKER_REQUESTS_QUEUE_LINK,
} from "../../paths";
import { reviewableActionCount } from "../../utils/corporateActions";
import { formatAgeHours, plural } from "../../utils/format";
import {
  AttentionAction,
  AttentionItem,
  AttentionKind,
  AttentionSeverity,
  DeriveAttentionItemsInput,
} from "../types";

const SEVERITY_ORDER: Record<AttentionSeverity, number> = {
  critical: 0,
  warning: 1,
  review: 2,
};

const VIEW_JOBS: AttentionAction = {
  label: "View jobs",
  href: JOBS_PATH,
};

const ATTENTION_ACTIONS: Record<AttentionKind, AttentionAction> = {
  coverage: { label: "View blocked jobs", href: BLOCKED_JOBS_LINK },
  snapshotQueue: VIEW_JOBS,
  backfillQueue: VIEW_JOBS,
  schedulers: { label: "View schedulers", href: SCHEDULERS_PATH },
  tickerRequests: {
    label: "Review requests",
    href: TICKER_REQUESTS_QUEUE_LINK,
  },
  instrumentMetadata: {
    label: "Fix instruments",
    href: INSTRUMENT_FIXES_QUEUE_LINK,
  },
  corporateActions: {
    label: "Review actions",
    href: CORPORATE_ACTIONS_QUEUE_LINK,
  },
};

// Pending hides deferred actions, so a row with nothing else would open on an empty view.
const VIEW_DEFERRED_ACTIONS: AttentionAction = {
  label: "View deferred",
  href: DEFERRED_CORPORATE_ACTIONS_QUEUE_LINK,
};

const isProblemStatus = (
  status: QueueHealthStatus | SchedulerHealthStatus,
): status is "critical" | "warning" =>
  status === "critical" || status === "warning";

const joinParts = (parts: (string | false | null)[]): string =>
  parts.filter(Boolean).join(" · ");

const snapshotQueueContext = ({
  failedJobs,
  pendingJobs,
  oldestPendingJob,
  oldestCoverageBlockedJob,
}: SnapshotQueueResponse): string =>
  joinParts([
    failedJobs > 0 && `${failedJobs} failed`,
    pendingJobs > 0 && `${pendingJobs} pending`,
    oldestPendingJob && `oldest ${formatAgeHours(oldestPendingJob.ageHours)}`,
    oldestCoverageBlockedJob &&
      `oldest blocked ${formatAgeHours(oldestCoverageBlockedJob.ageHours)}`,
  ]);

const backfillQueueContext = ({
  queuedJobs,
  exhaustedJobs,
  rateLimitedJobs,
  oldestQueuedJob,
  oldestStaleLease,
}: BackfillQueueResponse): string =>
  joinParts([
    exhaustedJobs > 0 && `${exhaustedJobs} exhausted`,
    rateLimitedJobs > 0 && `${rateLimitedJobs} rate-limited`,
    oldestStaleLease &&
      `stale lease ${formatAgeHours(oldestStaleLease.ageHours)}`,
    queuedJobs > 0 && `${queuedJobs} queued`,
    oldestQueuedJob &&
      `oldest queued ${formatAgeHours(oldestQueuedJob.ageHours)}`,
  ]);

const reviewItems = (todo: AdminTodoResponse): AttentionItem[] => {
  const {
    pendingTickerRequests,
    pendingTickerRequesters,
    oldestPendingTickerRequestAgeHours,
    instrumentsWithoutSourceCountry,
    instrumentsWithoutPricing,
    pendingCorporateActions,
    notYetEffectiveCorporateActions,
    deferredCorporateActions,
  } = todo;
  const items: AttentionItem[] = [];

  if (pendingTickerRequests > 0) {
    items.push({
      kind: "tickerRequests",
      severity: "review",
      title: "Ticker requests",
      context: [
        joinParts([
          `${pendingTickerRequests} pending`,
          oldestPendingTickerRequestAgeHours !== null &&
            `oldest ${formatAgeHours(oldestPendingTickerRequestAgeHours)}`,
        ]),
        `${plural(pendingTickerRequesters, "requester")} waiting`,
      ],
      action: ATTENTION_ACTIONS.tickerRequests,
    });
  }

  if (instrumentsWithoutSourceCountry > 0 || instrumentsWithoutPricing > 0) {
    items.push({
      kind: "instrumentMetadata",
      severity: "review",
      title: "Instrument metadata",
      context: [
        joinParts([
          instrumentsWithoutSourceCountry > 0 &&
            `${instrumentsWithoutSourceCountry} without source country`,
          instrumentsWithoutPricing > 0 &&
            `${instrumentsWithoutPricing} without pricing`,
        ]),
      ],
      action: ATTENTION_ACTIONS.instrumentMetadata,
    });
  }

  if (pendingCorporateActions > 0) {
    const reviewable = reviewableActionCount(todo);
    const isOnlyDeferred =
      reviewable === 0 && notYetEffectiveCorporateActions === 0;
    items.push({
      kind: "corporateActions",
      severity: "review",
      title: "Corporate actions",
      context: [
        joinParts([
          reviewable > 0 && `${reviewable} pending review`,
          notYetEffectiveCorporateActions > 0 &&
            `${notYetEffectiveCorporateActions} not yet effective`,
          deferredCorporateActions > 0 &&
            `${deferredCorporateActions} deferred`,
        ]),
      ],
      action: isOnlyDeferred
        ? VIEW_DEFERRED_ACTIONS
        : ATTENTION_ACTIONS.corporateActions,
    });
  }

  return items;
};

export const deriveAttentionItems = ({
  snapshotQueue,
  backfillQueue,
  coverageImpact,
  schedulers,
  todo,
}: DeriveAttentionItemsInput): AttentionItem[] => {
  const items: AttentionItem[] = [];

  if (coverageImpact && coverageImpact.blockedJobs > 0) {
    const { blockedInstruments, blockedJobs, blockedUsers, unattributedUsers } =
      coverageImpact;
    items.push({
      kind: "coverage",
      severity: "critical",
      title: "Blocked snapshots",
      context: [
        joinParts([
          plural(blockedInstruments, "instrument"),
          plural(blockedJobs, "snapshot"),
          plural(blockedUsers, "user"),
        ]),
        ...(unattributedUsers > 0
          ? [
              `${unattributedUsers} of these users could not be attributed to an instrument.`,
            ]
          : []),
      ],
      action: ATTENTION_ACTIONS.coverage,
    });
  }

  if (snapshotQueue && isProblemStatus(snapshotQueue.status)) {
    items.push({
      kind: "snapshotQueue",
      severity: snapshotQueue.status,
      title: "Snapshot queue",
      context: [snapshotQueueContext(snapshotQueue)],
      action: ATTENTION_ACTIONS.snapshotQueue,
    });
  }

  if (backfillQueue && isProblemStatus(backfillQueue.status)) {
    items.push({
      kind: "backfillQueue",
      severity: backfillQueue.status,
      title: "Backfill queue",
      context: [backfillQueueContext(backfillQueue)],
      action: ATTENTION_ACTIONS.backfillQueue,
    });
  }

  const silent = (schedulers ?? []).filter(({ status }) =>
    isProblemStatus(status),
  );
  if (silent.length > 0) {
    items.push({
      kind: "schedulers",
      severity: silent.some(({ status }) => status === "critical")
        ? "critical"
        : "warning",
      title: "Schedulers",
      context: [silent.map(({ name }) => name).join(", ")],
      action: ATTENTION_ACTIONS.schedulers,
    });
  }

  if (todo) {
    items.push(...reviewItems(todo));
  }

  return items
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        SEVERITY_ORDER[a.item.severity] - SEVERITY_ORDER[b.item.severity] ||
        a.index - b.index,
    )
    .map(({ item }) => item);
};
